import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { toast } from 'sonner';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import EmptyState from '../../components/ui/EmptyState';
import { useAuth } from '../../contexts/AuthContext';
import { siteService } from '../../services/siteService';
import { companyService } from '../../services/companyService';
import { complianceService } from '../../services/complianceService';
import { useLanguage } from '../../contexts/LanguageContext';
import SiteOverviewCard from './components/SiteOverviewCard';
import TeamManagementPanel from './components/TeamManagementPanel';
import TaskProgressPanel from './components/TaskProgressPanel';
import SafetyOverviewPanel from './components/SafetyOverviewPanel';
import SiteHealthPanel from './components/SiteHealthPanel';
import ExpiringDocumentsCard from '../../components/ui/ExpiringDocumentsCard';
import ComplianceWatch from '../admin-dashboard/components/ComplianceWatch';
import SitePhotoUploadModal from './components/SitePhotoUploadModal';
import RecentSitePhotosPanel from './components/RecentSitePhotosPanel';
import TrialBanner from '../../components/TrialBanner';
import SubscriptionReadOnlyBanner from '../../components/SubscriptionReadOnlyBanner';
import useSubscriptionReadOnly from '../../hooks/useSubscriptionReadOnly';
import useSiteReadOnly from '../../hooks/useSiteReadOnly';
import SiteReadOnlyBanner from '../../components/SiteReadOnlyBanner';

// Tab → route mapping (explicit, no wrong handlers)
const TAB_ROUTES = {
  documents: '/document-management',
  drawings: '/drawings-plans',
  snags: '/snags',
  incidents: '/incident-reporting',
  reports: '/report-builder',
};

const SiteManagerDashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { userProfile } = useAuth();
  const { isReadOnly: subscriptionReadOnly } = useSubscriptionReadOnly();
  // activeSite state is declared below; pass its id (or its known status) reactively.
  const { t } = useLanguage();

  const [activeTab, setActiveTab] = useState(searchParams?.get('tab') || 'overview');
  const [sites, setSites] = useState([]);
  const [activeSite, setActiveSite] = useState(null);
  const { status: activeSiteStatus, isReadOnly: activeSiteReadOnly } = useSiteReadOnly(
    activeSite?.id || null,
    activeSite?.status,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [complianceAlerts, setComplianceAlerts] = useState([]);
  const [siteScores, setSiteScores] = useState([]);
  const [complianceRawData, setComplianceRawData] = useState({});
  const [complianceLoading, setComplianceLoading] = useState(false);
  const [photoUploadOpen, setPhotoUploadOpen] = useState(false);
  // Bumped after a successful upload so RecentSitePhotosPanel re-fetches
  // without a full page reload. Plain incrementing key — no optimistic
  // prepend, since site-direct uploads need a freshly-signed URL anyway.
  const [photoRefreshKey, setPhotoRefreshKey] = useState(0);
  const [company, setCompany] = useState(null);

  // ─── Translated arrays (inside component to access t()) ───────────────────
  const NAV_TABS = [
    { id: 'overview', labelKey: 'smTabOverview', icon: 'LayoutDashboard' },
    { id: 'documents', labelKey: 'smTabDocuments', icon: 'FileText' },
    { id: 'drawings', labelKey: 'smTabDrawings', icon: 'PenTool' },
    { id: 'snags', labelKey: 'smTabSnags', icon: 'Wrench' },
    { id: 'incidents', labelKey: 'smTabIncidents', icon: 'AlertOctagon' },
    { id: 'reports', labelKey: 'smTabReports', icon: 'FileBarChart' },
  ];

  const QUICK_ACTIONS = [
    { labelKey: 'smQaAddPhoto', icon: 'Camera', color: 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100', action: 'addPhoto' },
    { labelKey: 'smQaCreateSnag', icon: 'Wrench', color: 'bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100', route: '/snags' },
    { labelKey: 'smQaReportIncident', icon: 'AlertOctagon', color: 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100', route: '/incident-reporting' },
    { labelKey: 'smQaAssignTask', icon: 'ClipboardList', color: 'bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100', route: '/task-planner' },
    { labelKey: 'smQaSiteDiary', icon: 'BookOpen', color: 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100', route: '/site-diary-management' },
    { labelKey: 'smQaRAMS', icon: 'FileCheck', color: 'bg-accent/10 text-accent border-accent/20 hover:bg-accent/20', route: '/rams-management' },
    { labelKey: 'smQaInductions', icon: 'GraduationCap', color: 'bg-teal-50 text-teal-700 border-teal-200 hover:bg-teal-100', route: '/site-inductions-management' },
    { labelKey: 'smQaToolboxTalks', icon: 'MessageSquare', color: 'bg-violet-50 text-violet-700 border-violet-200 hover:bg-violet-100', route: '/toolbox-talks-management' },
    { labelKey: 'smQaCertifications', icon: 'Award', color: 'bg-yellow-50 text-yellow-700 border-yellow-200 hover:bg-yellow-100', route: '/worker-certifications' },
    { labelKey: 'smQaCompliance', icon: 'ShieldCheck', color: 'bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100', route: '/compliance-dashboard' },
    { labelKey: 'smQaNearMisses', icon: 'AlertTriangle', color: 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100', route: '/near-misses' },
  ];

  const COMPLIANCE_ACTIONS = [
    { labelKey: 'smComplianceSiteDiary', icon: 'BookOpen', status: 'completed', route: '/daily-site-checklist', color: 'text-emerald-600' },
    { labelKey: 'smComplianceDailyPhotos', icon: 'Camera', status: 'pending', action: 'addPhoto', color: 'text-amber-600' },
    { labelKey: 'smComplianceToolboxSign', icon: 'PenLine', status: 'pending', route: '/document-management', color: 'text-amber-600' },
    { labelKey: 'smComplianceSiteChecks', icon: 'ClipboardCheck', status: 'overdue', route: '/daily-site-checklist', color: 'text-red-600' },
  ];

  const getStatusLabel = (status) => {
    if (status === 'completed') return t('smStatusCompleted');
    if (status === 'overdue') return t('smStatusOverdue');
    return t('smStatusPending');
  };
  // ──────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    loadData();
  }, []);

  // Re-fetch when the user returns to this tab (e.g. after admin assigns a site)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        loadData();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);

      // Load company for trial status
      const companyResult = await companyService?.getUserCompany();
      if (companyResult?.data) {
        setCompany(companyResult?.data);
      }

      // site_manager is scoped to assigned sites only (BwT3Rzhb).
      // No company-wide fallback: zero assignments = zero sites + empty state.
      const directResult = await siteService?.getSitesByUser();
      const siteList = directResult?.data || [];

      setSites(siteList);
      if (siteList?.length > 0) {
        // Preserve the user's current selection across refetches (mount,
        // visibilitychange, tab-nav + return). In-component state (`prev`)
        // handles same-mount refetches; localStorage handles the unmount
        // case — tabs like "Documents" navigate out of the dashboard
        // entirely, so on return `prev` is null and we fall back to the
        // last id the user picked.
        setActiveSite(prev => {
          if (prev && siteList?.some(s => s?.id === prev?.id)) return prev;
          const stored = localStorage?.getItem('lastActiveSiteId');
          if (stored && siteList?.some(s => s?.id === stored)) {
            return siteList?.find(s => s?.id === stored);
          }
          // Stored id no longer corresponds to a visible site (archived,
          // unassigned, wrong company after switch). Clear it so we don't
          // keep trying to restore it forever.
          if (stored) localStorage?.removeItem('lastActiveSiteId');
          return siteList?.[0];
        });
        loadComplianceData();
      }
    } catch (err) {
      console.error('[SiteManagerDashboard] loadData exception:', err);
      setError('Failed to load dashboard. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  const loadComplianceData = async () => {
    try {
      setComplianceLoading(true);
      const { data, error: compErr } = await complianceService?.fetchComplianceData();
      if (compErr || !data) return;
      const alerts = complianceService?.buildAlerts(data);
      const scores = complianceService?.computeSiteScores({ ...data, alerts });
      setComplianceAlerts(alerts);
      setSiteScores(scores);
      setComplianceRawData(data);
    } catch (err) {
      console.error('[SiteManagerDashboard] loadComplianceData error:', err);
    } finally {
      setComplianceLoading(false);
    }
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    if (tabId !== 'overview' && TAB_ROUTES?.[tabId]) {
      navigate(TAB_ROUTES?.[tabId]);
    }
  };

  const handleQuickAction = (action) => {
    if (action?.action === 'addPhoto') {
      setPhotoUploadOpen(true);
    } else if (action?.route) {
      navigate(action?.route);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent" />
        </div>
      </div>
    );
  }

  // No assigned sites — site_manager is assignment-scoped (BwT3Rzhb).
  // Show an explanatory empty state instead of a dashboard of empty panels.
  if (sites?.length === 0) {
    return (
      <div className="min-h-screen bg-background">
        <Helmet>
          <title>Site Manager Dashboard — SiteProof</title>
        </Helmet>
        <Header />
        <main className="pt-16">
          <div className="max-w-screen-2xl mx-auto px-3 sm:px-4 lg:px-8 py-10">
            <div className="bg-card rounded-lg shadow-elevation-2 border border-border">
              <EmptyState
                icon="Building2"
                title={t('noSitesAssignedManager')}
                description={t('noSitesAssignedManagerDesc')}
              />
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Trial lock check for site managers
  // Trial lock check removed in [B9UB0M58]. See admin-dashboard for full
  // rationale — banner now owns the canceled/unpaid/paused state.

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Site Manager Dashboard — SiteProof</title>
      </Helmet>
      <Header />
      <main className="pt-16">
        {/* Page Header */}
        <div className="bg-card border-b border-border">
          <div className="max-w-screen-2xl mx-auto px-3 sm:px-4 lg:px-8">
            <div className="flex items-start sm:items-center justify-between py-4 sm:py-5 gap-3">
              <div className="min-w-0">
                <h1 className="text-lg sm:text-2xl font-bold text-foreground font-heading">{t('siteManagerDashboard')}</h1>
                <p className="text-xs sm:text-sm text-muted-foreground mt-0.5 truncate">
                  {userProfile?.full_name || t('siteManagerSubtitle')} · {
                    sites?.length > 1
                      ? t('siteManagerManagingSites')?.replace('{n}', sites?.length)
                      : sites?.length === 1
                        ? (activeSite?.site_name || sites?.[0]?.site_name)
                        : t('siteManagerNoSite')
                  }
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {/* Site selector */}
                {sites?.length > 1 && (
                  <select
                    value={activeSite?.id || ''}
                    onChange={e => {
                      const site = sites?.find(s => s?.id === e?.target?.value);
                      setActiveSite(site);
                      if (site?.id) localStorage?.setItem('lastActiveSiteId', site?.id);
                    }}
                    className="rounded-lg border border-border bg-background px-2 sm:px-3 py-2 text-xs sm:text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-accent max-w-[120px] sm:max-w-none"
                  >
                    {sites?.map(s => (
                      <option key={s?.id} value={s?.id}>{s?.site_name}</option>
                    ))}
                  </select>
                )}
                <Button
                  variant="primary"
                  size="sm"
                  iconName="AlertOctagon"
                  iconPosition="left"
                  onClick={() => navigate('/incident-reporting')}
                  className="text-xs sm:text-sm whitespace-nowrap"
                >
                  <span className="hidden xs:inline sm:inline">{t('siteManagerReportIncident')}</span>
                  <span className="xs:hidden sm:hidden">Report</span>
                </Button>
              </div>
            </div>

            {/* Navigation Tabs — horizontal scroll on mobile */}
            <div className="flex items-center gap-1 overflow-x-auto pb-px scrollbar-hide -mx-3 sm:mx-0 px-3 sm:px-0">
              {NAV_TABS?.map(tab => (
                <button
                  key={tab?.id}
                  onClick={() => handleTabChange(tab?.id)}
                  className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-3 text-xs sm:text-sm font-medium whitespace-nowrap border-b-2 transition-colors shrink-0 ${
                    activeTab === tab?.id
                      ? 'border-accent text-accent' :'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                  }`}
                >
                  <Icon name={tab?.icon} size={14} />
                  {t(tab?.labelKey)}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="max-w-screen-2xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-6 space-y-4 sm:space-y-6">

          {/* Trial Banner */}
          <TrialBanner company={company} isDemoAdmin={false} />
          <SubscriptionReadOnlyBanner className="mb-6" />
          <SiteReadOnlyBanner status={activeSiteStatus} siteId={activeSite?.id} className="mb-6" />

          {/* Quick Actions Strip — 2-col on mobile, 6-col on sm+ */}
          <div>
            <h2 className="text-xs sm:text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">{t('quickActions')}</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2 sm:gap-3">
              {QUICK_ACTIONS?.map(action => (
                <button
                  key={action?.labelKey}
                  onClick={() => handleQuickAction(action)}
                  className={`flex flex-col items-center gap-2 p-3 rounded-xl border font-medium text-xs transition-all min-h-[72px] ${action?.color}`}
                >
                  <Icon name={action?.icon} size={20} />
                  <span className="text-center leading-tight">{t(action?.labelKey)}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Stats Cards Row — single-column on mobile */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <button
              onClick={() => navigate('/document-management')}
              className="bg-card rounded-xl border border-border shadow-elevation-1 p-4 text-left hover:border-accent/50 transition-colors"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center">
                  <Icon name="FileText" size={18} className="text-blue-600" />
                </div>
                <span className="text-xs font-medium text-muted-foreground">{t('smCardDocuments')}</span>
              </div>
              <p className="text-lg font-bold text-foreground">{t('smCardViewAll')}</p>
              <p className="text-xs text-blue-600 mt-0.5">→ /document-management</p>
            </button>

            <button
              onClick={() => navigate('/snags')}
              className="bg-card rounded-xl border border-border shadow-elevation-1 p-4 text-left hover:border-accent/50 transition-colors"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-9 h-9 rounded-lg bg-orange-50 flex items-center justify-center">
                  <Icon name="Wrench" size={18} className="text-orange-600" />
                </div>
                <span className="text-xs font-medium text-muted-foreground">{t('smCardOpenSnags')}</span>
              </div>
              <p className="text-lg font-bold text-foreground">{t('smCardViewAll')}</p>
              <p className="text-xs text-orange-600 mt-0.5">→ /snags</p>
            </button>

            <button
              onClick={() => navigate('/drawings-plans')}
              className="bg-card rounded-xl border border-border shadow-elevation-1 p-4 text-left hover:border-accent/50 transition-colors"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-9 h-9 rounded-lg bg-purple-50 flex items-center justify-center">
                  <Icon name="PenTool" size={18} className="text-purple-600" />
                </div>
                <span className="text-xs font-medium text-muted-foreground">{t('smCardDrawings')}</span>
              </div>
              <p className="text-lg font-bold text-foreground">{t('smCardViewAll')}</p>
              <p className="text-xs text-purple-600 mt-0.5">→ /drawings-plans</p>
            </button>

            <button
              onClick={() => navigate('/workers')}
              className="bg-card rounded-xl border border-border shadow-elevation-1 p-4 text-left hover:border-accent/50 transition-colors"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center">
                  <Icon name="Users" size={18} className="text-emerald-600" />
                </div>
                <span className="text-xs font-medium text-muted-foreground">{t('smCardWorkers')}</span>
              </div>
              <p className="text-lg font-bold text-foreground">{t('smCardViewAll')}</p>
              <p className="text-xs text-emerald-600 mt-0.5">→ /workers</p>
            </button>
          </div>

          {/* Main Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* On mobile: reorder so TaskProgress appears first for quick access */}
            {/* Left column */}
            <div className="space-y-6 order-2 lg:order-1">
              <SiteOverviewCard site={activeSite} />
              <SafetyOverviewPanel siteId={activeSite?.id} />
            </div>

            {/* Middle column */}
            <div className="space-y-6 order-3 lg:order-2">
              <TeamManagementPanel siteId={activeSite?.id} />
            </div>

            {/* Right column */}
            <div className="space-y-6 order-1 lg:order-3">
              <TaskProgressPanel siteId={activeSite?.id} />
              <SiteHealthPanel
                siteId={activeSite?.id}
                complianceAlerts={complianceAlerts}
                siteScores={siteScores}
                complianceRawData={complianceRawData}
              />
              <ExpiringDocumentsCard siteId={activeSite?.id} maxItems={4} />
            </div>
          </div>

          {/* Recent Photos for the active site */}
          {activeSite?.id && (
            <RecentSitePhotosPanel
              siteId={activeSite?.id}
              siteName={activeSite?.site_name}
              refreshKey={photoRefreshKey}
            />
          )}

          {/* Today's Compliance Actions */}
          <div className="bg-card rounded-xl border border-border shadow-elevation-2 overflow-hidden">
            <div className="px-6 py-4 border-b border-border">
              <h3 className="font-semibold text-foreground font-heading">{t('smComplianceActions')}</h3>
              <p className="text-sm text-muted-foreground">{t('smComplianceActionsSubtitle')}</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 divide-y sm:divide-y-0 sm:divide-x divide-border">
              {COMPLIANCE_ACTIONS?.map(item => (
                <button
                  key={item?.labelKey}
                  onClick={() => item?.action === 'addPhoto' ? setPhotoUploadOpen(true) : navigate(item?.route)}
                  disabled={item?.action === 'addPhoto' && (subscriptionReadOnly || activeSiteReadOnly)}
                  className="p-5 flex flex-col items-center gap-3 hover:bg-muted/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                >
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    item?.status === 'completed' ? 'bg-emerald-50' : item?.status === 'overdue' ? 'bg-red-50' : 'bg-amber-50'
                  }`}>
                    <Icon name={item?.icon} size={22} className={item?.color} />
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-medium text-foreground">{t(item?.labelKey)}</p>
                    <span className={`text-xs font-semibold capitalize ${
                      item?.status === 'completed' ? 'text-emerald-600' : item?.status === 'overdue' ? 'text-red-600' : 'text-amber-600'
                    }`}>
                      {getStatusLabel(item?.status)}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Compliance Watch - filtered to manager's assigned sites */}
          {sites?.length > 0 && (
            <ComplianceWatch
              alerts={complianceAlerts}
              siteScores={siteScores}
              loading={complianceLoading}
              complianceData={{ ...complianceRawData, alerts: complianceAlerts }}
              filteredSiteIds={sites?.map(s => s?.id)}
            />
          )}

          {error && (
            <div className="bg-destructive/10 border border-destructive/20 rounded-xl p-4 flex items-center gap-3">
              <Icon name="AlertCircle" size={18} className="text-destructive" />
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}
        </div>
      </main>

      {/* Site Photo Upload Modal */}
      <SitePhotoUploadModal
        isOpen={photoUploadOpen}
        onClose={() => setPhotoUploadOpen(false)}
        onSuccess={(count) => {
          // Modal already shows its own success state internally; this surfaces
          // confirmation on the dashboard once the modal closes, so the user
          // doesn't have to navigate to /site-photo-gallery to know it worked.
          const n = count || 1;
          toast.success(n === 1 ? 'Photo uploaded successfully' : `${n} photos uploaded successfully`);
          setPhotoRefreshKey(k => k + 1);
        }}
        siteId={activeSite?.id}
        siteName={activeSite?.site_name}
      />
    </div>
  );
};

export default SiteManagerDashboard;
